<template lang="pug">
Toolbar(class="header-bar" :class="showButton ? '' : 'full-width'")
  template(#start)
    .rating-wrapper
      .rating
        .rating-title
          .rating-icon
            GammaIcon(:src="getIconFromCSS('rating-status')" :default-src="BerHouseIcon" iconClass="'rating-status'")
          span {{ $t('ratingBar.title') }}
        .rating-content-wrapper
          .rating-content-loading-wrapper(v-if="isLoadingReport")
            Loading(
              :active="!recommendationsPageLoading && isLoadingReport"
              :is-full-page="false"
              loader="bars"
              :opacity="0"
              :color="primary ? primary : '#ccc'"
              :z-index="1000"
            )
          .rating-content(v-if="!isLoadingReport" :style="isLoadingReport ? 'display: none' : 'display: flex'")
            .hli HLI: {{ report?.resultsAnalysis.after.heatLossIndicator ? Math.round(report?.resultsAnalysis.after.heatLossIndicator * 100)/100 : 'N/A' }}
            RatingBox.rating-current(
              v-if="report"
              :label="settings?.devOpts?.showRatingValue ? `${$t('ratingBar.current')} - ${Math.floor(report.beforeRating.value)}` : $t('ratingBar.current')"
              :value="report.beforeRating.code"
            )
            RatingBox.rating-new(
              v-if="report"
              :label="settings?.devOpts?.showRatingValue ? `${$t('ratingBar.new')} - ${Math.floor(report.afterRating.value)}` : $t('ratingBar.new')"
              :value="report.afterRating.code"
            )
            .tour-wrapper(v-if="!settings?.tourOptions?.disableTour")
              TourButton(v-if="tourButtonPos == undefined | tourButtonPos === ButtonPosition.Default")
  template(#end)
    .button-wrapper(v-if="showButton")
      ScheduleButton
</template>
<script lang="ts">
import {computed} from "vue";
import {useRecommendations} from "@/store/recommendations";
import {defineComponent} from "vue";
import {storeToRefs} from "pinia";
import Toolbar from "primevue/toolbar";
import ScheduleButton from "@/components/result/ScheduleButton.vue";
import Loading from "vue-loading-overlay";
import BerwowLogo from "@/components/icons/berwow-logo.vue";
import {useRouter} from "vue-router";
import {getIconFromCSS, getRatingColor} from "@/helpers";
import GammaIcon from "@/components/icons/GammaIcon.vue";
import {useSettings} from "@/store";
import {ButtonPosition} from "@/types";
import RatingBox from "./rating/RatingBox.vue";
import TourButton from "./TourButton.vue";
import BerHouseIcon from '@/components/icons/ber-house-icon.vue'

export default defineComponent({
  name: "ScheduleBar",
  components: {
    Toolbar,
    ScheduleButton,
    Loading,
    BerwowLogo,
    GammaIcon,
    RatingBox,
    TourButton,
    BerHouseIcon,
  },
  setup() {
    const router = useRouter();
    const settingsStore = useSettings();
    const {initializeTour} = settingsStore;
    const {tour, settings} = storeToRefs(settingsStore);
    const {report, recommendationsPageLoading, isLoadingReport} = storeToRefs(
        useRecommendations()
    );
    const tourButtonPos = settings.value?.recommendationsPageOptions?.tourButtonPosition;

    async function startTour() {
      await router.replace("/recommendations");
      if (!tour.value) {
        initializeTour();
      }
      setTimeout(() => tour.value.start(), 250);
    }

    const primary = getComputedStyle(document.documentElement)
        .getPropertyValue("--berwow-primary-color")
        .trim();

    const showButton = computed(
        () =>
            settings.value &&
            (settings.value.recommendationsPageOptions?.actionButtonPosition == undefined ||
                settings.value.recommendationsPageOptions?.actionButtonPosition ===
                ButtonPosition.Default)
    );

    return {
      report,
      isLoadingReport,
      recommendationsPageLoading,
      getIconFromCSS,
      startTour,
      ButtonPosition,
      settings,
      showButton,
      getRatingColor,
      primary,
      tourButtonPos,
      BerHouseIcon,
    };
  },
});
</script>
<style scoped lang="scss">
@import "@/assets/styles/components/ber-rating.scss";

.header-bar {
  padding: 1rem 3rem;
  background-color: $background-color-body;
  border: none;

  .p-toolbar-group-right {
    flex-grow: 0.5;
  }

  @media screen and (min-width: 1200px) {
    &:not(.full-width):deep(.p-toolbar-group-start) {
      width: calc(100% - 26vw - 1rem);
    }

    &:not(.full-width):deep(.p-toolbar-group-end) {
      width: 26vw;
      max-width: 30rem;
    }

    &.full-width:deep(.p-toolbar-group-start) {
      width: 100%;
    }
  }
}

.rating-wrapper {
  border: $bar-border;
  box-shadow: $card-shadow;
  width: 100%;
}

.rating {
  background-color: $background-color-default;
  padding: 0.45rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-left: 1rem;
  cursor: default;

  .rating-content-wrapper {
    position: relative;
    min-height: 2.5rem;
    display: flex;
  }

  .rating-content {
    display: flex;
    align-items: center;

    .hli {
      display: none;
      color: $font-color-secondary;
    }
    .tour-wrapper {
      padding: 0.25rem;
    }
  }

  .rating-title {
    font-weight: bold;
    display: flex;
    align-items: center;
    gap: 0.75rem;

    .rating-icon {
      max-width: 2rem;
    }

    span {
      font-size: 1.4rem;
      font-variant-caps: all-small-caps;
      padding-left: 0.5rem;
      color: $primary-color-hover;
    }
  }

  .rating-content-loading-wrapper {
    min-width: 10rem;
  }
}

.button-wrapper {
  width: 100%;

  button {
    height: calc(3rem + 10px);
  }

  svg {
    display: none;
  }
}

@media screen and (max-width: 600px) {
  .rating-title {
    span {
      display: none;
    }
  }
}

@media screen and (max-width: 1200px) {
  .p-toolbar {
    flex-direction: column-reverse;
    align-items: stretch;
    padding: 0;

    :deep(.p-toolbar-group-start) {
      width: 100%;
    }

    .button-wrapper {
      display: none;

      button {
        display: none;
      }
    }

    .rating-wrapper {
      margin: 0;
      width: 100%;
      display: flex;

      .rating {
        width: 100%;
      }
    }
  }
}

@media screen and (max-width: 1120px) and (min-width: 720px) {
  .header-bar {
    padding: 1rem;
  }
}
</style>
